import { useState, useRef } from 'react';
import { Send, Smile, X, Image as ImageIcon, FileText, Video, Plus, Mic } from 'lucide-react';
import { useWhatsappWebMutations } from '../whatsappWebApi';
import { toast } from 'react-hot-toast';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';

interface MessageComposerProps {
  chatId: string;
}

export function MessageComposer({ chatId }: MessageComposerProps) {
  const [body, setBody] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachment, setAttachment] = useState<{ file: File; type: string; base64: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { send, sendMedia } = useWhatsappWebMutations();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('File size must be less than 15MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      if (!result) return;
      const base64Data = result.split(',')[1];
      setAttachment({
        file,
        type: file.type || 'application/octet-stream',
        base64: base64Data
      });
      setAttachMenuOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSend = () => {
    if (!body.trim() && !attachment) return;

    const to = chatId.replace('@c.us', '').replace('@g.us', '');

    if (attachment) {
      sendMedia.mutate(
        { 
          to, 
          body: body.trim(), 
          data: attachment.base64, 
          mimetype: attachment.type,
          filename: attachment.file.name 
        },
        {
          onSuccess: () => {
            setBody('');
            setAttachment(null);
          },
          onError: () => toast.error('Failed to send media')
        }
      );
    } else {
      send.mutate(
        { to, body: body.trim() },
        {
          onSuccess: () => setBody(''),
          onError: () => toast.error('Failed to send message')
        }
      );
    }
  };

  const isPending = send.isPending || sendMedia.isPending;

  return (
    <div className="flex flex-col bg-[#f0f2f5] px-4 py-2 relative h-[62px] justify-center shrink-0">
      {/* Attachment Preview */}
      {attachment && (
        <div className="absolute bottom-full left-0 right-0 bg-[#e9edef] border-t border-[#d1d7db] p-4 flex flex-col items-center justify-center shadow-lg min-h-[150px] z-10">
          <button 
            className="absolute top-4 right-4 p-2 bg-white rounded-full text-gray-500 shadow hover:bg-gray-50"
            onClick={() => setAttachment(null)}
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="bg-white p-4 rounded-lg shadow-sm max-w-sm w-full flex flex-col items-center gap-3">
            {attachment.type.startsWith('image/') ? (
              <img src={`data:${attachment.type};base64,${attachment.base64}`} alt="preview" className="max-h-48 rounded object-contain" />
            ) : attachment.type.startsWith('video/') ? (
              <div className="h-24 w-24 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                <Video className="h-10 w-10" />
              </div>
            ) : (
              <div className="h-24 w-24 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                <FileText className="h-10 w-10" />
              </div>
            )}
            <span className="text-body-sm text-gray-600 truncate w-full text-center">{attachment.file.name}</span>
            <p className="text-xs text-gray-400">Add a caption below before sending</p>
          </div>
        </div>
      )}

      {/* Input Row */}
      <div className="flex items-center gap-2 w-full max-w-[100%]">
        <button className="p-2 text-[#54656f] hover:text-[#111b21] shrink-0 transition-colors">
          <Smile className="h-[26px] w-[26px]" />
        </button>
        
        <div className="relative shrink-0">
          <button 
            className={cn(
              "p-2 text-[#54656f] hover:text-[#111b21] transition-colors rounded-full",
              attachMenuOpen && "bg-black/10"
            )}
            onClick={() => setAttachMenuOpen(!attachMenuOpen)}
          >
            <Plus className="h-[26px] w-[26px]" />
          </button>
          
          {/* Attachment Menu */}
          {attachMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setAttachMenuOpen(false)} />
              <div className="absolute bottom-[56px] left-[-24px] z-20 flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-[0_2px_5px_0_rgba(11,20,26,0.26),0_2px_10px_0_rgba(11,20,26,0.16)] w-[240px] animate-in fade-in slide-in-from-bottom-2">
                <button 
                  className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-[#f5f6f6] transition-colors text-[#111b21]"
                  onClick={() => { fileInputRef.current?.click(); }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#007bfc] text-white">
                    <FileText className="h-5 w-5" />
                  </div>
                  <span className="text-[16px]">Document</span>
                </button>
                <button 
                  className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-[#f5f6f6] transition-colors text-[#111b21]"
                  onClick={() => { fileInputRef.current?.click(); }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#005c4b] text-white">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                  <span className="text-[16px]">Photos & videos</span>
                </button>
              </div>
            </>
          )}
        </div>

        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          onChange={handleFileChange} 
          accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        />

        <div className="flex-1 rounded-[8px] bg-white px-3 py-[9px] shadow-sm min-h-[42px] flex items-center border border-white focus-within:border-[#00a884]">
          <textarea 
            rows={1}
            placeholder="Type a message"
            className="w-full resize-none border-none bg-transparent text-[15px] text-[#111b21] outline-none max-h-32 focus:ring-0 p-0 leading-tight"
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              // Auto-resize
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
        </div>

        <button 
          className="p-2 text-[#54656f] hover:text-[#111b21] shrink-0 disabled:opacity-50 transition-colors ml-1"
          onClick={handleSend}
          disabled={(!body.trim() && !attachment) || isPending}
        >
          {isPending ? (
            <Spinner className="h-[24px] w-[24px] text-[#54656f]" />
          ) : (body.trim() || attachment) ? (
            <Send className="h-[24px] w-[24px]" />
          ) : (
            <Mic className="h-[24px] w-[24px]" />
          )}
        </button>
      </div>
    </div>
  );
}
